import { useState } from "react";
import { toast } from "sonner";
import { API, authFetch, useAuth } from "@/App";
import { Check, X, Clock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * APPROVAL_ROLES - users who can approve/reject/hold
 */
export const APPROVAL_ROLES = ["SUPER_ADMIN", "FACTORY_ADMIN", "SALES_ADMIN"];

export const canApprove = (user) => !!user && APPROVAL_ROLES.includes(user.role);

/**
 * Default tag presets per entity type. Caller can override with `tags` prop.
 */
const DEFAULT_TAGS = {
  order: [
    "Stock Available", "Credit OK", "Price Verified", "Scheme Eligible",
    "Stock Unavailable", "Price Mismatch", "Payment Pending", "Credit Limit Exceeded",
    "Customer Request", "Scheme Violation", "Quantity Issue", "Documentation Pending",
    "Duplicate Order", "Awaiting Clarification", "Other",
  ],
  leave: [
    "Prior Notice Given", "Replacement Arranged", "Valid Reason", "Manager Approved",
    "Short Notice", "Peak Season", "No Replacement", "Pending Verification",
    "Insufficient Balance", "Documentation Required", "Medical Certificate Needed",
    "Repeat Request", "Other",
  ],
  expense: [
    "Valid Receipt", "Policy Compliant", "Client Related", "Travel OK",
    "Over Limit", "Missing Receipt", "Non-Reimbursable", "Duplicate Claim",
    "Needs Manager Sign-off", "Documentation Pending", "Unclear Purpose",
    "Personal Expense", "Other",
  ],
  scheme_claim: [
    "Target Achieved", "Documents Verified", "Performance Confirmed", "Bonus Approved",
    "Target Not Met", "Documents Missing", "Calculation Error", "Period Mismatch",
    "Dealer Inactive", "Awaiting Confirmation", "Manual Review Required", "Other",
  ],
  grievance: [
    "Valid Concern", "Action Taken", "Pending HR Review", "Resolved",
    "Insufficient Info", "Not Applicable", "Escalated", "Under Investigation",
    "Duplicate", "Other",
  ],
};

/**
 * <ApprovalActionDialog />
 *
 * A unified approval/rejection/hold dialog with mandatory reason-tag + optional notes.
 *
 * Props:
 *   open           boolean                  - dialog open state
 *   onClose        () => void               - callback to close
 *   entity         "order"|"leave"|"expense"|"scheme_claim"|"grievance"
 *   action         "APPROVE"|"REJECT"|"HOLD"
 *   entityId       string                   - id used in endpoint
 *   entityLabel    string                   - shown in header subtitle
 *   endpointBase   string                   - e.g. "/orders" → calls POST /orders/{id}/approve
 *   tags           string[] (optional)      - override default tag list
 *   onSuccess      () => void               - called after successful action
 *   customBody     object (optional)        - extra payload to merge
 *   actionLabel    string (optional)        - override button text
 */
export const ApprovalActionDialog = ({
  open,
  onClose,
  entity,
  action = "APPROVE",
  entityId,
  entityLabel = "",
  endpointBase,
  tags,
  onSuccess,
  customBody = {},
  actionLabel,
}) => {
  const { user } = useAuth();
  const [tag, setTag] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const resolvedTags = tags || DEFAULT_TAGS[entity] || DEFAULT_TAGS.order;

  const reset = () => { setTag(""); setNotes(""); };
  const handleClose = () => { reset(); onClose?.(); };

  const endpointSuffix =
    action === "APPROVE" ? "approve" :
    action === "REJECT"  ? "reject"  :
                           "hold";

  const submit = async () => {
    if (!tag) {
      toast.error("Please select a reason tag");
      return;
    }
    if (!canApprove(user)) {
      toast.error("Only Super Admin, Factory Admin, or Sales Admin can perform this action");
      return;
    }
    setSaving(true);
    try {
      // Build body with multiple common key aliases so the server accepts regardless
      // of which legacy field it expects (tag/notes/reason/rejection_reason)
      const body = {
        tag,
        notes,
        reason: notes || tag,
        rejection_reason: action === "REJECT" ? (notes || tag) : undefined,
        ...customBody,
      };
      const res = await authFetch(`${API}${endpointBase}/${entityId}/${endpointSuffix}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (d.success) {
        toast.success(`${entity.replace("_", " ")} ${action.toLowerCase()}d with tag: ${tag}`);
        onSuccess?.(d.data);
        handleClose();
      } else {
        toast.error(d.detail || d.error || `${action} failed`);
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const actionButtonClass =
    action === "APPROVE" ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
    action === "REJECT"  ? "bg-red-600 hover:bg-red-700 text-white" :
                           "bg-amber-600 hover:bg-amber-700 text-white";

  const Icon = action === "APPROVE" ? Check : action === "REJECT" ? X : Clock;

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="approval-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            {action === "APPROVE" && `Approve ${entity.replace("_", " ")}`}
            {action === "REJECT" && `Reject ${entity.replace("_", " ")}`}
            {action === "HOLD" && `Put ${entity.replace("_", " ")} on Hold`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {entityLabel && (
            <p className="text-sm text-slate-600">
              <strong>{entityLabel}</strong>
            </p>
          )}
          <div>
            <label className="text-xs font-medium text-slate-700">
              Reason Tag <span className="text-red-500">*</span>
            </label>
            <Select value={tag} onValueChange={setTag}>
              <SelectTrigger className="mt-1" data-testid="approval-tag-select">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {resolvedTags.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`approval-tag-opt-${t.replace(/\s/g, "-").toLowerCase()}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Notes (optional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              className="mt-1"
              data-testid="approval-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !tag}
            className={actionButtonClass}
            data-testid="approval-submit-btn"
          >
            {saving ? "Saving..." : (actionLabel || `Confirm ${action}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApprovalActionDialog;
